// Admin API functions

import type { SystemHealth, AdminMetrics, AdminUser } from './admin.schema';
import { apiFetch } from '../../shared/api/apiClient'; // attach API key for protected routes

// Fetch system health status
export async function fetchSystemHealth(): Promise<SystemHealth> {
  // corrected endpoint: health check routes are mounted at `/healthChk/`
  // rationale: aligns with routes/api/healthChk.ts via configureRoutes
  const response = await apiFetch('/healthChk/'); // base health check endpoint (public, no API key needed)
  if (!response.ok) {
    throw new Error('Failed to fetch system health');
  }
  const json = await response.json();
  return {
    status: json?.status === 'ok' ? 'healthy' : 'error',
    uptime: 0,
    memory: { used: 0, total: 1 },
    database: { connected: json?.status === 'ok', latency: 0 },
    services: [],
  };
}

// Fetch admin metrics
export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  // corrected endpoint: backend provides admin metrics via `/api/analytics/kpis`, protected by API key middleware
  // attach API key via apiFetch; if no key available, request is forwarded unchanged (may 401)
  try {
    const response = await apiFetch('/api/analytics/kpis'); // use wrapper to send Authorization/x-api-key headers when configured
    if (!response.ok) {
      throw new Error('Failed to fetch admin metrics');
    }
    const json = await response.json();
    const kpis = json?.data?.kpis || json?.result?.kpis || {};
    return {
      totalTransactions: kpis?.payments?.total30Days ?? 0,
      totalUsers: kpis?.subscriptions?.total ?? 0,
      revenue: kpis?.payments?.revenue30Days ?? 0,
      activeSubscriptions: kpis?.subscriptions?.active ?? 0,
    };
  } catch (error) {
    throw new Error('Failed to fetch admin metrics: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

// Fetch admin users
export async function fetchAdminUsers(): Promise<AdminUser[]> {
  // corrected endpoint: users list is exposed by @bijikyu/qryptauth-backed router at `/api/users`
  // rationale: align with newly mounted usersRoutes integrating `@bijikyu/qryptauth` library
  try {
    const response = await apiFetch('/api/users'); // updated to users listing with API key
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to fetch admin users');
    }
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch admin users: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

// Update user role
export async function updateUserRole(userId: string, role: string): Promise<{ success: boolean }> {
  // corrected endpoint: role updates are available at `/api/users/:id/role`
  // rationale: provided by usersRoutes backed by Mongo and `@bijikyu/qryptauth`
  try {
    const response = await apiFetch(`/api/users/${userId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });
    
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to update user role');
    }
    return response.data;
  } catch (error) {
    throw new Error('Failed to update user role: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}
